import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import type {
  FinancialProductOffer,
  FinancialProductsResponseBody,
  FinancialProductType,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import type {Nullish} from 'shared/web';

import {
  getConfiguredFinancialProducts,
  getFinancialProductOffer,
  getPrimaryFinancialProductType,
} from 'features/financial-products';

import {FINANCIAL_PRODUCT_ORDER} from './FINANCIAL_PRODUCT_ORDER';

export interface FinancialProductPreferenceViewModel {
  productType: FinancialProductType | null;
  offer: FinancialProductOffer | null;
  isPrimaryOption: boolean;
  hasNoProducts: boolean;
  isSnapshot: boolean;
  productName: string | null;
  updatedBy: FinancialProductPreferenceUpdatedByViewModel | null;
}

export type FinancialProductPreferenceUpdatedByViewModel =
  | {kind: 'CUSTOMER'}
  | {kind: 'SELLER'; sellerName: string}
  | {kind: 'REMOVED_SELLER'};

const PRODUCT_NAME_KEY = {
  STANDARD: 'page.vehicle.financingAndLeasing.standard',
  BALLOON: 'page.vehicle.financingAndLeasing.balloon',
  LEASING: 'financialProducts.product.leasing',
} as const;

const updatedByOf = (
  financialProducts: FinancialProductsResponseBody
): FinancialProductPreferenceUpdatedByViewModel | null =>
  match(financialProducts.preferenceUpdatedBy)
    .with({source: 'customer'}, () => ({kind: 'CUSTOMER'}) as const)
    .with(
      {source: 'seller', seller: Pattern.nonNullable},
      (updatedBy) => ({kind: 'SELLER', sellerName: updatedBy.seller.displayName}) as const
    )
    .with({source: 'seller'}, () => ({kind: 'REMOVED_SELLER'}) as const)
    .otherwise(() => null);

/** A selected product stays authoritative even when unavailable; only no selection may fall back. */
export function getFinancialProductPreferenceViewModel(
  financialProducts: FinancialProductsResponseBody | Nullish
): FinancialProductPreferenceViewModel {
  if (isNil(financialProducts)) {
    return {
      productType: null,
      offer: null,
      isPrimaryOption: false,
      hasNoProducts: true,
      isSnapshot: false,
      productName: null,
      updatedBy: null,
    };
  }

  const configuredProducts = getConfiguredFinancialProducts(
    financialProducts,
    FINANCIAL_PRODUCT_ORDER
  );
  const selectedProduct = financialProducts.selectedProduct;
  const productType =
    getPrimaryFinancialProductType(financialProducts, FINANCIAL_PRODUCT_ORDER) ?? null;

  return {
    productType,
    offer: isNotNil(productType) ? getFinancialProductOffer(financialProducts, productType) : null,
    isPrimaryOption: isNil(selectedProduct) && isNotNil(productType),
    hasNoProducts: configuredProducts.length === 0 && isNil(selectedProduct),
    isSnapshot: financialProducts.mode === 'CONTRACT_SNAPSHOT',
    productName: isNotNil(productType) ? i18n.t(PRODUCT_NAME_KEY[productType]) : null,
    updatedBy: updatedByOf(financialProducts),
  };
}
